import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Banknote, FileBarChart, BookOpen, Settings, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Toaster } from "../components/ui/sonner";

const ALL_NAV = [
  { key: "beranda", to: "/", label: "Beranda", icon: LayoutDashboard, testid: "nav-beranda" },
  { key: "stok", to: "/stok", label: "Stok", icon: Package, testid: "nav-stok" },
  { key: "pembelian", to: "/pembelian", label: "Beli", icon: ShoppingCart, testid: "nav-pembelian" },
  { key: "penjualan", to: "/penjualan", label: "Jual", icon: Banknote, testid: "nav-penjualan" },
  { key: "jurnal", to: "/jurnal", label: "Jurnal", icon: BookOpen, testid: "nav-jurnal" },
  { key: "laporan", to: "/laporan", label: "Laporan", icon: FileBarChart, testid: "nav-laporan" },
  { key: "pengaturan", to: "/pengaturan", label: "Atur", icon: Settings, testid: "nav-pengaturan" },
];

const NAV_ROLES = {
  beranda: ["administrator"],
  stok: ["administrator", "admin"],
  pembelian: ["administrator", "admin"],
  penjualan: ["administrator", "kasir"],
  jurnal: ["administrator", "kasir"],
  laporan: ["administrator", "audit"],
  pengaturan: ["administrator"],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navItems = ALL_NAV.filter((n) => NAV_ROLES[n.key]?.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-white/85 backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-white font-bold text-sm" style={{ fontFamily: "'Work Sans', sans-serif" }}>GE</div>
            <div>
              <p className="text-sm font-bold tracking-tight leading-none" style={{ fontFamily: "'Work Sans', sans-serif" }}>Griya Elektronik</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold">Jual Beli Bekas</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                className={({ isActive }) => `rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${isActive ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <span className="hidden sm:block text-xs text-muted-foreground" data-testid="user-email">{user?.email}</span>
            <button onClick={handleLogout} data-testid="logout-button" className="rounded-md p-2 text-muted-foreground hover:text-destructive transition-colors duration-150 active:scale-95" title="Keluar">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pt-5 pb-24 md:pb-10">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white/85 backdrop-blur-xl backdrop-saturate-150 md:hidden" data-testid="bottom-nav">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}>
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottom-${n.testid}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors duration-150 active:scale-95 ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <Toaster position="top-center" richColors />
    </div>
  );
}
